import { ComponentProps, useContext, useMemo } from "react";
import { useRouter } from "next/router";
import { Interpolation, Theme, css, useTheme } from "@emotion/react";
import { Empty } from "components/core/Empty";
import { HorizontalPicker } from "components/core/HorizontalPicker";
import { Tooltip } from "components/core/Tooltip";
import { IconButton } from "components/core/buttons/IconButton";
import { ContentGridMixContentQuery } from "components/domain/contents/ContentGridMixContentQuery";
import { ContentGridQuery } from "components/domain/contents/ContentGridQuery";
import { SectionScaffold } from "components/domain/contents/sections/SectionScaffold";
import { useMixArchives } from "../hooks/useMixArchives";
import { AppContext } from "contexts/AppContext";
import { useAuth } from "hooks/useAuth";
import { fireEvent } from "lib/ga";
import { theme as staticTheme } from "styles/theme";
import { zIndex } from "styles/zIndex";
import { LIMIT } from "utils/buildQueryVariables";
import {
  DEVICE_TYPE,
  MIX_CONTENT_TAG_ENUM_KOREAN,
  ORDERED_MIX_CONTENT_TAG_ENUM,
  DEFAULT_TAG_LIST,
  SHINHAN_INVEST_TAG_LIST,
  TRIAL_TAG_LIST,
} from "utils/enum";
import { useSessionQuery, useGetUserInterestTagsQuery } from "graphql/generated";
import { ContentSection } from "types/app";

export const RecentSection = ({
  contentSection,
  variables,
  ...props
}: Omit<
  ComponentProps<
    typeof ContentGridQuery<MixContentQuery, MixContentQueryVariables>
  >,
  "useQuery" | "extractor" | "updateQuery"
> & {
  css?: Interpolation<Theme>;
  contentSection: ContentSection;
  variables: MixContentQueryVariables;
}) => {
  const router = useRouter();
  const { me, isLoggedIn } = useAuth();
  const { deviceType } = useContext(AppContext);
  const theme = useTheme();
  const { archives, archiveTagEnums, archivesLoading } = useMixArchives();

  const { data: sessionData } = useSessionQuery();
  const userId = sessionData?.session?.me?.id;
  const userGroup = sessionData?.session?.me?.group_id;

  const { data: userInterestTagsData } = useGetUserInterestTagsQuery({
    variables: { userId: userId! },
    skip: !userId,
  });

  const tag = useMemo(
    () =>
      ORDERED_MIX_CONTENT_TAG_ENUM.find((tag) => tag === router.query.tag) ??
      null,
    [router],
  );

  // Apply the same filtering logic as desktop for mobile category display
  const displayTags = useMemo(() => {
    let tags: any[] = [];

    // For logged-out users, exclude Shinhan investment categories
    if (!isLoggedIn) {
      tags = ORDERED_MIX_CONTENT_TAG_ENUM.filter(
        (tag) => !SHINHAN_INVEST_TAG_LIST.includes(tag as any),
      );
    }
    // For logged-in users, apply the same logic as desktop
    // 1. 쿼리에서 유효한 태그가 있으면 해당 태그만 반환
    else if (
      typeof router.query.tag === "string" &&
      ORDERED_MIX_CONTENT_TAG_ENUM.includes(router.query.tag as any)
    ) {
      tags = [router.query.tag as any];
    }
    // 2. 유저 관심 태그가 있으면, 해당 태그 리스트 반환 (ONLY user's selected categories)
    else {
      const userTags = userInterestTagsData?.mix_user_interest_tag;
      if (userTags?.length) {
        tags = userTags.map(
          (item) => item.tag_value as any,
        );
      } else {
        // 3. userGroup별 분기 (only when user has no preferences)
        switch (userGroup) {
          case "master":
            tags = ORDERED_MIX_CONTENT_TAG_ENUM; // 전체 보기
            break;
          case "shinhaninvest":
            tags = [
              ...DEFAULT_TAG_LIST,
              ...SHINHAN_INVEST_TAG_LIST,
            ] as any[];
            break;
          case "general":
          case "thesmc":
          case undefined:
            tags = DEFAULT_TAG_LIST as any[];
            break;
          default:
            tags = DEFAULT_TAG_LIST as any[];
            break;
        }
      }
    }

    // Filter out trial tags from all results
    return tags.filter((tag) => !TRIAL_TAG_LIST.includes(tag as any));
  }, [isLoggedIn, router.query.tag, userInterestTagsData, userGroup]);

  return (
    <SectionScaffold {...props}>
      <SectionScaffold.Header>
        <SectionScaffold.Title>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
            `}
          >
            <span
              css={css`
                white-space: nowrap;
                overflow: hidden;
                color: ${theme.palette.text.primary};
              `}
            >
              콘텐츠 큐레이션
            </span>
            <Tooltip
              placement="top"
              trigger={
                deviceType !== DEVICE_TYPE.DESKTOP ? "mousedown" : "mouseover"
              }
            >
              <Tooltip.Trigger asChild={false}>
                <IconButton
                  name="MoreHoriz"
                  size="medium"
                  color="grey"
                  variant="background"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    e.stopPropagation();
                    router.push(
                      {
                        ...router,
                        hash: me ? "#interest-tags" : "#signin",
                      },
                      undefined,
                      { scroll: false },
                    );
                  }}
                  css={css`
                    flex-shrink: 0;
                  `}
                />
              </Tooltip.Trigger>

              <Tooltip.Content
                css={css`
                  max-width: 260px;
                  padding: 8px 10px 2px;
                  border-radius: 6px;
                  font-size: 12px;
                  word-break: keep-all;
                `}
              >
                <p
                  css={css`
                    font-weight: 500;
                    white-space: pre-wrap;
                    margin-bottom: 6px;
                  `}
                >
                  관심 주제 설정
                </p>
              </Tooltip.Content>
            </Tooltip>
          </div>
          {tag !== null && (
            <span
              css={css`
                margin-left: 12px;
                color: ${theme.palette.text.placeholder};

                @media (max-width: 1024px) {
                  display: none;
                }
              `}
            >
              #{MIX_CONTENT_TAG_ENUM_KOREAN[tag]}
            </span>
          )}
        </SectionScaffold.Title>
      </SectionScaffold.Header>
      <SectionScaffold.Contents>
        <HorizontalPicker<Mix_Content_Tag_Enum_Enum | null>
          css={css`
            display: none;

            @media (max-width: 1024px) {
              position: sticky;
              top: calc(var(--header-height) + 1px);
              background: ${(theme as any).mode === "dark"
              ? theme.palette.common.black.main
              : theme.palette.common.white.main
            };
              display: block;
              z-index: ${zIndex.categoryPicker};
              padding-bottom: ${theme.shape.radius.medium}px;
              margin-bottom: 16px;
              padding-top: 0.6rem;
            }
          `}
          items={[
            { value: null, label: "전체" },
            ...displayTags.reduce(
              (
                acc: { value: Mix_Content_Tag_Enum_Enum; label: string }[],
                value,
              ) => {
                const label = MIX_CONTENT_TAG_ENUM_KOREAN[value];
                if (label) {
                  acc.push({ value, label });
                }
                return acc;
              },
              [],
            ),
          ]}
          value={tag}
          onChange={(value) => {
            if (value) {
              router.push(
                process.env.BUILD_TARGET === "extension"
                  ? `https://mix.day?tag=${value}`
                  : `?tag=${value}`,
              );

              fireEvent({
                event: "content_category_click",
                params: {
                  value,
                },
              });
            } else {
              router.push(
                process.env.BUILD_TARGET === "extension"
                  ? `https://mix.day`
                  : `/`,
              );
            }
          }}
        />
        <ContentGridMixContentQuery
          type="mixed"
          contentSection={contentSection}
          variables={variables}
          limit={LIMIT.default}
          archives={archives}
          archiveTagEnums={archiveTagEnums}
          archivesLoading={archivesLoading}
          isGuest={!isLoggedIn}
          empty={
            <Empty
              imgurl="/img/content-empty.png"
              mainText="추천할 만한 콘텐츠를 열심히 찾고 있어요."
              subText="조금만 기다려 주세요!"
            />
          }
        />
      </SectionScaffold.Contents>
    </SectionScaffold>
  );
};
